import { useEffect, useState } from "react";
import { Plus, Edit, Trash2, X } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";
import ImageUpload from "../../components/ImageUpload";

const empty = { name: "", description: "", image_url: "", order: 0, is_active: true };

export default function AdminFlavours() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(empty);

    const load = () => api.get("/flavours?active_only=false").then((r) => setItems(r.data));
    useEffect(() => { load(); }, []);

    const openNew = () => { setEditing("new"); setForm(empty); };
    const openEdit = (f) => { setEditing(f.id); setForm(f); };
    const close = () => { setEditing(null); setForm(empty); };

    const save = async (e) => {
        e.preventDefault();
        try {
            if (editing === "new") await api.post("/flavours", form);
            else await api.put(`/flavours/${editing}`, form);
            toast.success("Saved");
            close();
            load();
        } catch (err) {
            toast.error("Save failed");
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this flavour?")) return;
        try { await api.delete(`/flavours/${id}`); toast.success("Deleted"); load(); }
        catch { toast.error("Delete failed"); }
    };

    return (
        <div data-testid="admin-flavours">
            <div className="flex items-center justify-between mb-6">
                <h2 className="font-display text-3xl">Flavours</h2>
                <button onClick={openNew} className="btn-pink" data-testid="add-flavour">
                    <Plus size={14} /> New
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {items.map((f, i) => (
                    <div key={f.id} className="bg-white border border-[#EAE3DE] p-4 flex gap-4" data-testid={`flavour-row-${i}`}>
                        <img src={f.image_url} alt={f.name} className="w-20 h-20 object-cover flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                            <p className="font-display text-lg truncate">{f.name}</p>
                            <p className="text-xs text-ink-soft line-clamp-2 mb-2">{f.description}</p>
                            <div className="flex gap-2">
                                <button onClick={() => openEdit(f)} className="text-xs text-ink hover:text-blush" data-testid={`edit-flavour-${i}`}><Edit size={14} /></button>
                                <button onClick={() => remove(f.id)} className="text-xs text-ink hover:text-blush" data-testid={`delete-flavour-${i}`}><Trash2 size={14} /></button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {editing && (
                <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
                    <div className="bg-white w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="font-display text-2xl">{editing === "new" ? "New" : "Edit"} Flavour</h3>
                            <button onClick={close}><X size={20} /></button>
                        </div>
                        <form onSubmit={save} className="space-y-4">
                            <input className="w-full px-4 py-3 border border-[#EAE3DE]" placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
                            <textarea className="w-full px-4 py-3 border border-[#EAE3DE]" rows={3} placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} required />
                            <ImageUpload value={form.image_url} onChange={(v) => setForm({ ...form, image_url: v })} label="Flavour Image" testIdPrefix="flavour-image" />
                            <div className="grid grid-cols-2 gap-3">
                                <input type="number" className="w-full px-4 py-3 border border-[#EAE3DE]" placeholder="Order" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })} />
                                <label className="flex items-center gap-2">
                                    <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
                                    <span className="text-sm">Active</span>
                                </label>
                            </div>
                            <button type="submit" className="btn-pink w-full">Save</button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
